import { useState, useRef } from 'react';
import { CheckCircleIcon } from '@heroicons/react/outline';
import mmjCardService from '@/services/mmjCard.service';

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
  'application/pdf',
];

const UploadZone = ({ label, file, previewUrl, onFileSelect, uploading }) => {
  const inputRef = useRef();

  const handleClick = () => {
    inputRef.current.click();
  };

  const handleChange = (e) => {
    const selected = e.target.files[0];
    if (selected) onFileSelect(selected);
  };

  return (
    <div className="flex-1">
      {label && (
        <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
      )}
      <div
        onClick={handleClick}
        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-mmj-blue transition-colors"
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleChange}
        />
        {uploading ? (
          <div className="py-6 text-gray-500">Uploading...</div>
        ) : previewUrl ? (
          <div>
            <img
              src={previewUrl}
              alt={label || 'Uploaded'}
              className="max-h-32 mx-auto rounded mb-2"
            />
            <div className="text-xs text-gray-500">Click to replace</div>
          </div>
        ) : file ? (
          <div className="py-4">
            <div className="text-sm text-gray-700">{file.name}</div>
            <div className="text-xs text-gray-500">Click to replace</div>
          </div>
        ) : (
          <div className="py-6">
            <div className="text-sm text-gray-500">Click to select a file</div>
            <div className="text-xs text-gray-400 mt-1">
              JPG, PNG, HEIC, WebP, or PDF
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FileUpload = ({
  state,
  cardId,
  existingUrls,
  onCardCreated,
  onSuccess,
  patientId,
  platformId,
}) => {
  const isGA = state === 'ga';
  const [files, setFiles] = useState(isGA ? [null, null] : [null]);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(
    existingUrls && existingUrls.length > 0,
  );
  const [error, setError] = useState(null);

  const handleFileSelect = (index) => (file) => {
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
  };

  const handleUpload = async () => {
    const filesToUpload = files.filter(Boolean);
    if (filesToUpload.length === 0) return;
    if (isGA && filesToUpload.length < 2) {
      setError('Please upload both the front and back of your ID.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileUrls = [];

      for (const file of filesToUpload) {
        const {
          data: { upload_url, file_url },
        } = await mmjCardService.getPresignedUrl(file.name, file.type);
        await mmjCardService.uploadToS3(upload_url, file, file.type);
        fileUrls.push(file_url);
      }

      if (cardId) {
        await mmjCardService.updateCard(cardId, {
          patient_document_urls: fileUrls,
        });
      } else {
        const { data } = await mmjCardService.createCard({
          state,
          patient_id: patientId,
          platform_id: platformId,
          mmj_issued: false,
          patient_document_urls: fileUrls,
        });
        if (onCardCreated) onCardCreated(data.uid);
      }

      setSubmitted(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.log(err);
    } finally {
      setUploading(false);
    }
  };

  const heading =
    state === 'ga'
      ? 'Upload Your State ID (Front & Back)'
      : 'Upload Your State ID';

  const subtext =
    state === 'ga'
      ? "Please upload photos of the FRONT and BACK of your driver's license or state ID. Your doctor needs these to register you in Georgia's Low THC Oil Registry."
      : "Please upload a photo of your driver's license, state ID, or temporary ID. Your doctor needs this to submit your certification.";

  if (submitted) {
    return (
      <div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-3">
            <CheckCircleIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
            <div className="font-bold text-lg text-green-800">
              State ID Submitted
            </div>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Your documents have been received. Your doctor has been notified. No
            further action is needed on your part for this step.
          </p>
          <button
            type="button"
            className="text-sm text-gray-500 hover:underline"
            onClick={() => setSubmitted(false)}
          >
            Need to upload different files?
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-bold text-lg mb-1">{heading}</h3>
      <p className="text-sm text-gray-600 mb-4">{subtext}</p>
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        {isGA ? (
          <>
            <UploadZone
              label="Front of ID"
              file={files[0]}
              previewUrl={files[0] ? URL.createObjectURL(files[0]) : null}
              onFileSelect={handleFileSelect(0)}
              uploading={uploading}
            />
            <UploadZone
              label="Back of ID"
              file={files[1]}
              previewUrl={files[1] ? URL.createObjectURL(files[1]) : null}
              onFileSelect={handleFileSelect(1)}
              uploading={uploading}
            />
          </>
        ) : (
          <UploadZone
            file={files[0]}
            previewUrl={files[0] ? URL.createObjectURL(files[0]) : null}
            onFileSelect={handleFileSelect(0)}
            uploading={uploading}
          />
        )}
      </div>
      {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
      <button
        type="button"
        onClick={handleUpload}
        disabled={uploading || !files.some(Boolean)}
        className={`w-full py-3 rounded-lg text-white font-medium text-base transition-colors ${
          !uploading && files.some(Boolean)
            ? 'bg-mmj-blue hover:opacity-90'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
      >
        {uploading ? 'Uploading...' : 'Submit'}
      </button>
    </div>
  );
};

export default FileUpload;
